import React, { useCallback, useEffect, useRef, useState } from "react";
import BatchDetails from "./BatchDetails";
import { fetchAssignees, fetchBatchLeads, updateAssignments } from "../callWorkspaceApi";
import { getStoredUser, getToken, isAssignmentAdmin, toCallWorkspaceLead } from "../callWorkspace.utils";
import { getApiErrorMessage } from "utils/getApiErrorMessage";

const BatchDetailsContainer = ({ match, history }) => {
  const batchId = Number(match.params.batchId);
  const [batch, setBatch] = useState(null);
  const [leads, setLeads] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [assignmentLead, setAssignmentLead] = useState(null);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState("");
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [assignmentError, setAssignmentError] = useState("");
  const [assigneesLoading, setAssigneesLoading] = useState(true);
  const [assigneesError, setAssigneesError] = useState("");
  const [assignee, setAssignee] = useState("all");
  const [status, setStatus] = useState("all");
  const [statsData, setStatsData] = useState({});
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const requestId = useRef(0);
  const canManageAssignments = isAssignmentAdmin(getStoredUser());

  const load = useCallback((page) => {
    const id = ++requestId.current;
    setLoading(true);
    setError("");
    const params = { page: page || 1, page_size: 20 };
    if (assignee !== "all") params.assignee = assignee;
    if (status !== "all") {
      params.status = {
        "Not called": "not_called",
        "No answer": "no_answer",
        "Follow-up": "follow_up",
        "Called": "called",
        "Don't Call": "blacklisted",
      }[status];
    }
    return fetchBatchLeads(getToken(), batchId, params)
      .then((response) => {
        if (requestId.current !== id) return;
        const result = response.data || {};
        const pageData = result.leads || {};
        setBatch(result.batch || null);
        setStatsData(result.stats || {});
        setLeads((pageData.data || []).map((lead) => toCallWorkspaceLead(lead)));
        setPagination({ current_page: pageData.current_page || 1, last_page: pageData.last_page || 1, total: pageData.total || 0 });
      })
      .catch((apiError) => { if (requestId.current === id) setError(getApiErrorMessage(apiError)); })
      .finally(() => { if (requestId.current === id) setLoading(false); });
  }, [assignee, batchId, status]);

  useEffect(() => {
    document.title = "Batch Leads | Call Workspace";
    setAssignee("all");
    setStatus("all");
    setAssigneesLoading(true);
    setAssigneesError("");
    fetchAssignees(getToken())
      .then((response) => setAssignees(response.data || []))
      .catch((apiError) => setAssigneesError(getApiErrorMessage(apiError)))
      .finally(() => setAssigneesLoading(false));
  }, [batchId]);

  useEffect(() => {
    load(1);
    return () => { requestId.current += 1; };
  }, [load]);

  const stats = [
    { label: "Not called", count: statsData.not_called || 0, total: statsData.total || 0 },
    { label: "No answer", count: statsData.no_answer || 0, total: statsData.total || 0 },
    { label: "Follow-up", count: statsData.follow_up || 0, total: statsData.total || 0 },
    { label: "Interested", count: statsData.interested || 0, total: statsData.total || 0 },
    { label: "Closed", count: statsData.closed || 0, total: statsData.total || 0 },
    { label: "Don't Call", count: statsData.blacklisted || 0, total: statsData.total || 0 },
  ];

  const openLeadAssignment = (lead) => {
    setAssignmentLead(lead);
    setSelectedAssigneeId(String(lead.assigneeId || ""));
    setAssignmentError("");
  };

  const saveLeadAssignment = (event) => {
    event.preventDefault();
    if (!assignmentLead || assignmentSaving) return;
    setAssignmentSaving(true);
    setAssignmentError("");
    updateAssignments(getToken(), {
      task_ids: [assignmentLead.taskId],
      assigned_to: selectedAssigneeId ? Number(selectedAssigneeId) : null,
    })
      .then(() => {
        setAssignmentLead(null);
        load(pagination.current_page);
      })
      .catch((apiError) => setAssignmentError(getApiErrorMessage(apiError)))
      .finally(() => setAssignmentSaving(false));
  };

  return (
    <BatchDetails
      batch={batch}
      leads={leads}
      stats={stats}
      assignees={assignees}
      assigneesLoading={assigneesLoading}
      assigneesError={assigneesError}
      onRetryAssignees={() => {
        setAssigneesLoading(true);
        setAssigneesError("");
        fetchAssignees(getToken())
          .then((response) => setAssignees(response.data || []))
          .catch((apiError) => setAssigneesError(getApiErrorMessage(apiError)))
          .finally(() => setAssigneesLoading(false));
      }}
      canManageAssignments={canManageAssignments}
      assignmentLead={assignmentLead}
      selectedAssigneeId={selectedAssigneeId}
      assignmentSaving={assignmentSaving}
      assignmentError={assignmentError}
      onAssignLead={openLeadAssignment}
      onSelectAssignee={setSelectedAssigneeId}
      onSaveLeadAssignment={saveLeadAssignment}
      onCloseAssignment={() => {
        if (!assignmentSaving) {
          setAssignmentLead(null);
          setAssignmentError("");
        }
      }}
      assignee={assignee}
      status={status}
      loading={loading}
      error={error}
      pagination={pagination}
      onRetry={() => load(pagination.current_page)}
      onPageChange={load}
      onAssigneeChange={setAssignee}
      onStatusChange={setStatus}
      onClearFilters={() => { setAssignee("all"); setStatus("all"); }}
      onBack={() => history.push("/admin/call-workspace/batch-management")}
      onOpenLead={(lead) => history.push("/admin/lead/" + lead.leadId + "?taskId=" + lead.taskId)}
    />
  );
};

export default BatchDetailsContainer;
